export const DEFAULT_PAYMENT_LINK_ADJUSTABLE_MAXIMUM = 99;
export const PAYMENT_LINK_ADJUSTABLE_MAXIMUM_LIMIT = 999_999;

export interface PaymentLinkAfterCompletion {
  type?: string;
  hosted_confirmation?: {
    custom_message?: string | null;
  };
  redirect?: {
    url?: string;
  };
}

export interface PaymentLinkCustomFieldLengthLimit {
  minimum_length?: number | string | null;
  maximum_length?: number | string | null;
}

export interface PaymentLinkCustomField {
  key?: string;
  type: string;
  label: {
    type?: string;
    custom: string;
  };
  optional?: boolean;
  text?: PaymentLinkCustomFieldLengthLimit;
  numeric?: PaymentLinkCustomFieldLengthLimit;
  dropdown?: {
    options: { value: string; label: string }[];
  };
}

export interface PaymentLinkRestrictions {
  completed_sessions?: {
    limit: number;
  };
}

export interface PaymentLinkConsentCollection {
  promotions?: string;
  terms_of_service?: string;
}

export interface PaymentLinkAdjustableQuantity {
  enabled: boolean;
  maximum?: number;
  minimum?: number;
}

export interface PaymentLinkLineItemPayload {
  id?: string;
  price?: string;
  quantity?: number;
  adjustable_quantity?: PaymentLinkAdjustableQuantity;
}

export interface PaymentLinkPayload {
  metadata?: Record<string, string | number>;
  line_items: PaymentLinkLineItemPayload[];
  optional_items?: PaymentLinkLineItemPayload[];
  useInventory?: boolean;
  locationId?: string;
  automatic_tax?: { enabled: boolean };
  custom_fields?: PaymentLinkCustomField[];
  after_completion?: PaymentLinkAfterCompletion;
  restrictions?: PaymentLinkRestrictions;
  consent_collection?: PaymentLinkConsentCollection;
  custom_text?: Record<string, string>;
  inactive_message?: string;
  submit_type?: string;
  active?: boolean;
}

export type PaymentLinkEditPayload = Partial<Omit<PaymentLinkPayload, "line_items">> & {
  line_items?: PaymentLinkLineItemPayload[];
};

export interface SendPaymentLinkPayload {
  name?: string;
  email?: string;
  phone?: string;
  paymentLink?: string;
  paymentLinkId?: string;
  customerId?: string;
  methods?: string;
  businessName?: string;
}

export type { PaymentLinkDesiredItem } from "@nectarpay/api-contracts/payment-link";
import type { PaymentLinkDesiredItem } from "@nectarpay/api-contracts/payment-link";

export interface PaymentLinks {
  id: string;
  active: boolean;
  after_completion: PaymentLinkAfterCompletion | null;
  currency: string;
  custom_fields: PaymentLinkCustomField[];
  inactive_message: string;
  submit_type: string;
  restrictions: PaymentLinkRestrictions | null;
  consent_collection: PaymentLinkConsentCollection | null;
  url: string;
  metadata: Record<string, string> & {
    name: string;
    created: string;
  };
  line_items: {
    data: PaymentLinkLineItem[];
  };
  optional_items?: PaymentLinkLineItemPayload[] | null;
  automatic_tax?: { enabled?: boolean };
  inventory?: {
    enabled?: boolean;
    locationId?: string | null;
    blocked?: boolean;
    blockReason?: string | null;
    permanentlyInvalid?: boolean;
    invalid?: boolean;
  };
  nectarpay?: {
    merchantActive?: boolean;
    useInventory?: boolean;
    locationId?: string | null;
    desiredItems?: PaymentLinkDesiredItem[];
    effectiveItems?: PaymentLinkDesiredItem[];
    preTaxRange?: { lower: number; upper: number; currency?: string };
    syncInvalid?: boolean;
  };
}

export interface PaymentLinkLineItem {
  id: string;
  quantity: number;
  description?: string;
  adjustable_quantity?: PaymentLinkAdjustableQuantity | null;
  price: {
    id: string;
    nickname?: string;
    currency?: string;
    unit_amount?: number | null;
    product?:
      | string
      | {
          id: string;
          name?: string;
          images?: string[];
          [key: string]: unknown;
        };
    [key: string]: unknown;
  };
}

export interface SelectedPaymentLinkLineItem extends PaymentLinkLineItem {
  productId?: string;
  priceId?: string;
  optional?: boolean;
  isAvailableInStripe?: boolean;
  isAdjustable: boolean;
  adjustable: string | undefined;
  configuredQuantity?: number;
  configuredAdjustableQuantity?: Required<PaymentLinkAdjustableQuantity>;
  available?: boolean;
  effectiveQuantity?: number;
  effectiveMaximum?: number;
  availabilityReason?: string | null;
}

export interface PaymentLinkProductAssociation {
  id: string;
  stripePaymentLinkId?: string;
  name?: string;
  url?: string;
  optional: boolean;
  priceId: string;
  locationId?: string;
  stripeActive?: boolean;
  inventory?: {
    enabled?: boolean;
    locationId?: string | null;
    blocked?: boolean;
  };
}

export interface ListPaymentLinksResponse {
  data: PaymentLinks[];
  has_more: boolean;
}

// Rows rendered by BaseStripeDataTable come straight from the list API and
// include backend-decorated fields beyond the PaymentLinks shape.
export interface PaymentLinkTableRow {
  id: string;
  name?: string;
  url?: string;
  grandTotal?: number;
  currency?: string;
  active?: boolean;
  metadata?: {
    name?: string;
    created?: number;
  };
}

export interface ListPaymentLinkLineItemsResponse {
  data: PaymentLinkLineItem[];
  has_more?: boolean;
}

export interface ListActivePaymentLinksApiOptions {
  limit?: number;
  starting_after?: string;
}

export interface PaymentLinksStore {
  loading: boolean;
  selectedPaymentLink: PaymentLinks | null;
  paymentLinks: PaymentLinks[];
  allPaymentLinks: PaymentLinks[];
  isActive: boolean | null;
  loadingListAllPaymentLinks: boolean;
  paymentLinkPayload: PaymentLinkPayload;
  creatingPaymentLinkLoading: boolean;
  creationKey: string | null;
  selectedLineItems: SelectedPaymentLinkLineItem[];
  editPaymentLinkPayload: PaymentLinkEditPayload;
  hasMore: boolean;
  startingAfter: string;
  endingBefore: string;
  metadataName: string;
  paymentLinkValidation: {
    customFields: boolean;
    confirmationPage: boolean;
    paymentLimit: boolean;
  };
}
